import axios, { AxiosRequestConfig, AxiosRequestHeaders, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { BASE_URL } from '../../utils/constants';
import { getToken } from '../../utils/devUtils';
import { camelizeKeys } from "humps";
import { InfiniteData } from '@tanstack/react-query';
import { OneServiceT, PaginationT } from '../types/services';

const baseURL = `${BASE_URL}/api/v1`;

export const instance = axios.create({
  baseURL,
  withCredentials: false,
  headers: { 'Content-Type': 'application/json' },
});

export const instanceWithoutTransform = axios.create({
  baseURL,
  withCredentials: false,
  headers: { 'Content-Type': 'application/json' },
});

export const bareInstance = axios.create({
  baseURL,
  withCredentials: false,
  headers: { 'Content-Type': 'application/json' },
});

export const formDataApi = axios.create({
  baseURL,
  headers: {
    Accept: "application/json",
    "Content-Type": "multipart/form-data",
  },
  maxRedirects: 0,
});

export const fileDataInstance = axios.create({
  baseURL,
  withCredentials: false,
  headers: {
    'Content-Type': 'application/octet-stream',
  }
});

export const removeEmptyValues = (obj: UnknownRecord): Record<string, unknown> => {
  if (!obj) return {};
  return Object.keys(obj).reduce((acc, key) => {
    const value = obj[key];
    if (typeof value === 'object' && value !== null) {
      const newValue = Array.isArray(value) ? value.map(removeEmptyValues) : removeEmptyValues(value as UnknownRecord);
      if (Object.keys(newValue).length !== 0) {
        acc[key] = newValue;
      }
    } else if (value !== undefined && value !== null && value !== '') {
      acc[key] = value;
    }
    return acc;
  }, {} as UnknownRecord);
};

const setAuthToken = (config: InternalAxiosRequestConfig) => {
  const token = getToken();
  if (token) {
    return {
      ...config,
      headers: {
        ...config.headers,
        Authorization: `Bearer ${token}`,
      } as AxiosRequestHeaders,
    };
  }
  return config;
};

const formatDataToParams = (params: AxiosRequestConfig['params']): UnknownRecord => {
  if (!params) return params;
  return Object.entries(params).reduce((prev, [key, value]) => ({
    ...prev,
    [key]: Array.isArray(value)
      ? value.join(',')
      : typeof value === 'object' && value !== null
        ? formatDataToParams(value)
        : value,
  }), {});
}

export const camelizeResponse = (data: AxiosResponse['data']): UnknownRecord => {
  return camelizeKeys(data, (key, convert) => {
    return /^[A-Z]+$/.test(key) ? key : convert(key);
  })
};

formDataApi.interceptors.request.use(setAuthToken);
formDataApi.interceptors.response.use((response) => response.data.data);

fileDataInstance.interceptors.request.use(setAuthToken);
fileDataInstance.interceptors.response.use((response) => response.data);

instance.interceptors.request.use((config) => {
  return {
    ...config,
    data: removeEmptyValues(config.data),
    url: config?.url?.replace(/[^=&]+=(?:&|$)/g, ''),
    params: removeEmptyValues(formatDataToParams(config.params)),
  }
});
instance.interceptors.request.use(setAuthToken);
instance.interceptors.response.use((response) => response.data.data);

bareInstance.interceptors.request.use((config) => {
  if (config.method === 'delete' && config.data) {
    return ({
      ...config,
      data: config.data,
      url: config?.url?.replace(/[^=&]+=(?:&|$)/g, ''),
      params: removeEmptyValues(formatDataToParams(config.params)),
    })
  }

  return ({
    ...config,
    data: removeEmptyValues(config.data),
    url: config?.url?.replace(/[^=&]+=(?:&|$)/g, ''),
    params: removeEmptyValues(formatDataToParams(config.params)),
  })
});
bareInstance.interceptors.response.use((response) => response.data.data);

instanceWithoutTransform.interceptors.request.use((config) => ({
  ...config,
  url: config?.url?.replace(/[^=&]+=(?:&|$)/g, ''),
  params: removeEmptyValues(formatDataToParams(config.params)),
}));
instanceWithoutTransform.interceptors.request.use(setAuthToken);


export const paginationReponseToGeneral = (data: InfiniteData<{
  items: OneServiceT["response"][];
  pagination: PaginationT;
}, FixMeLater>) => {
  return ({
    ...data,
    pages: data.pages.map((page) => {
      const pagination = camelizeResponse((page as FixMeLater).pagination) as PaginationT;
      return {
        data: (page as FixMeLater).items,
        page: pagination.currentPage,
        size: pagination.perPage,
        total: pagination.totalItems
      };
    })
  })
}